import React, { useEffect, useState, useRef } from "react";

const StatBox = ({ target, label, suffix = "+" }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (boxRef.current) observer.observe(boxRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const duration = 1600;
    const steps = duration / 16;
    const increment = target / steps;

    const interval = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.ceil(start));
      }
    }, 16);
    return () => clearInterval(interval);
  }, [started, target]);

  return (
    <div className="statBox" ref={boxRef}>
      <div className="statNumber">
        {count}
        <span className="statSuffix">{suffix}</span>
      </div>
      <div className="statLabel">{label}</div>
    </div>
  );
};

export default function StatsCounter() {
  const stats = [
    { target: 13, label: "Years of Cumulative Experience" },
    { target: 100, label: "Satisfied Students" },
    { target: 150, label: "Recorded Lessons" },
    { target: 45, label: "Weekly New Users" },
  ];

  return (
    <section className="statsSection">
      <div className="statsWrapper">
        {stats.map((item, index) => (
          <React.Fragment key={item.label}>
            <StatBox target={item.target} label={item.label} />
            {index !== stats.length - 1 && <div className="statDivider" />}
          </React.Fragment>
        ))}
      </div>

      <style>{`
        .statsSection {
          background-color: #ffffff;
          padding: 36px 20px;
          width: 100%;
          box-sizing: border-box;
        }

        .statsWrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: 0;
          max-width: 1100px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 8px 26px rgba(15, 23, 42, 0.08);
          border: 1px solid #eef2f7;
          padding: 8px 0;
        }

        .statBox {
          flex: 1 1 200px;
          text-align: center;
          padding: 24px 16px;
          transition: transform 0.3s ease;
        }

        .statBox:hover {
          transform: translateY(-4px);
        }

        .statNumber {
          font-size: 1.9rem;
          font-weight: 800;
          color: #1d4ed8;
          font-family: 'Segoe UI', Arial, sans-serif;
          line-height: 1.1;
        }

        .statSuffix {
          color: #f7941d;
          font-weight: 800;
        }

        .statLabel {
          margin-top: 8px;
          font-size: 11.5px;
          font-weight: 600;
          color: #64748b;
          letter-spacing: 0.2px;
          font-family: 'Segoe UI', Arial, sans-serif;
        }

        .statDivider {
          width: 1px;
          height: 46px;
          background: #e2e8f0;
        }

        /* Tablet */
        @media (max-width: 900px) {
          .statNumber { font-size: 1.7rem; }
        }

        /* Mobile — 2x2 GRID */
        @media (max-width: 768px) {
          .statsSection { padding: 28px 14px; }

          .statsWrapper {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0;
            padding: 6px;
          }

          .statBox {
            width: 100%;
            padding: 18px 10px;
            border-bottom: 1px solid #eef2f7;
          }

          /* right column items get a left divider */
          .statBox:nth-child(odd) {
            border-right: 1px solid #eef2f7;
          }

          /* last row items: no bottom border */
          .statBox:nth-last-child(-n + 2) {
            border-bottom: none;
          }

          /* hide the old inline divider on mobile grid */
          .statDivider {
            display: none;
          }

          .statNumber { font-size: 1.5rem; }
          .statLabel { font-size: 10.5px; }
        }

        @media (max-width: 420px) {
          .statNumber { font-size: 1.3rem; }
          .statLabel { font-size: 10px; }
          .statBox { padding: 16px 8px; }
        }
      `}</style>
    </section>
  );
}